import React from 'react';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectMarketPrice } from 'src/store/slice/marketPrice/state';
import { selectCreatedQuote } from 'src/store/slice/quote/state';
import { IPaymentItem, IPaymentTransactionItem } from 'src/store/slice/payment/interface';

import { ETransactionStatus } from 'src/type/transaction';

import Steps from 'src/components/transactions/steps';
import Timer from 'src/components/timer';
import ButtonCopy, { EButtonCopyType } from 'src/components/buttonCopy';

import './transaction.scss';

interface ITransaction {
  title: any;
  transaction?: IPaymentTransactionItem;
  payment?: IPaymentItem | null;
  isSetedDefaultInvoiceCurrency: boolean;
  isRequesSuccess: boolean;
  isOpenDepositInvoice?: boolean;
}

const Transaction: React.FC<ITransaction> = ({
  title,
  transaction,
  payment,
  isSetedDefaultInvoiceCurrency,
  isRequesSuccess,
  isOpenDepositInvoice = false,
}): JSX.Element => {
  const { t } = useTranslation();

  const createdQuote = useSelector(selectCreatedQuote);
  const marketPrice = useSelector(selectMarketPrice);

  const { expiration } = createdQuote || marketPrice || { expiration: 0 };
  const { currency_id, passthrough_fees } = payment || {};

  const isCompletedTransaction = transaction?.status === ETransactionStatus.Completed;
  const isEmptyTransaction = !transaction || !transaction.status;
  const isPassthroughFees = passthrough_fees;

  const sourceAmountReceived = transaction
    ? new BigNumber(transaction.source_amount_received.toString()).toFormat()
    : '';
  const estimatedTargetAmount = transaction
    ? new BigNumber(transaction.estimated_target_amount.toString()).toFormat()
    : '';

  const currentFeeAmount= transaction?.sub_merchant_fee_amount 
  ? new BigNumber(transaction.sub_merchant_fee_amount).toFormat() 
  : '';

  return (
    <li className="transactions-item">
      <h3 className="transactions-item-title">{title}</h3>
      {!isSetedDefaultInvoiceCurrency && !!expiration && isEmptyTransaction && (
        <Timer
          expiration={expiration}
          isRequesSuccess={isRequesSuccess}
        />
      )}

      {transaction && (
        <p className="amount-receive">
          {sourceAmountReceived} {transaction?.source_currency_id} {transaction?.source_currency_network}
          {!isOpenDepositInvoice && ` ( ${estimatedTargetAmount} ${currency_id} )`}
        </p>
      )}

      {transaction?.crypto_transaction_id && (
        <p className="has-transaction-id">
          TX ID: <span className="has-transaction-id-hash">{transaction?.crypto_transaction_id}</span>
          <ButtonCopy
            text={transaction?.crypto_transaction_id}
            type={EButtonCopyType.Icon}
          />
        </p>
      )}

      <Steps
        isComplete={isCompletedTransaction}
        stepNumber={transaction?.confirmations}
      />

      {/* Комиссия для Default Invoice */}
      {!isOpenDepositInvoice && !!currentFeeAmount && !isPassthroughFees && (
        <p className="transactions-item-fee">
          {t('TRANSACTION.TRANSACTION_FEE_ABSOLUTE', {
            fee: currentFeeAmount,
            currency: transaction?.source_currency_id,
          })}
        </p>
      )}

      {/* Комиссия для Open Deposit */}
      {!!currentFeeAmount && isOpenDepositInvoice && isRequesSuccess && !isPassthroughFees && (
        <p className="transactions-item-fee">
          {t('TRANSACTION.TRANSACTION_FEE_ABSOLUTE', {
            fee: currentFeeAmount,
            currency: transaction?.source_currency_id,
          })}
        </p>
      )}
    </li>
  );
};

export default Transaction;
